import { DEFAULT_THEME_AND_COLORS } from "../constants/defaultBranding";
import type { ThemeAndColors } from "../types";

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function isValidHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR_PATTERN.test(value.trim());
}

function normalizeColor(value: unknown, fallback: string): string {
  return isValidHexColor(value) ? value.trim() : fallback;
}

function expandHex(hex: string): string {
  const body = hex.slice(1);
  if (body.length === 3) {
    return body
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return body;
}

function relativeLuminance(hex: string): number {
  const expanded = expandHex(hex);
  const value = Number.parseInt(expanded, 16);
  const channels = [
    (value >> 16) & 255,
    (value >> 8) & 255,
    value & 255,
  ].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** Minimum contrast vs white for secondary body text (WCAG large-text threshold). */
const MIN_SECONDARY_TEXT_CONTRAST_ON_WHITE = 3;

function contrastRatioOnWhite(textHex: string): number {
  return 1.05 / (relativeLuminance(textHex) + 0.05);
}

function normalizeSecondaryFontColor(value: unknown, fallback: string): string {
  const candidate = normalizeColor(value, fallback);
  if (contrastRatioOnWhite(candidate) >= MIN_SECONDARY_TEXT_CONTRAST_ON_WHITE) {
    return candidate;
  }
  return fallback;
}

export function normalizeThemeAndColors(
  themeAndColors?: Partial<ThemeAndColors> | null,
): ThemeAndColors {
  const primaryColor = normalizeColor(
    themeAndColors?.primaryColor,
    DEFAULT_THEME_AND_COLORS.primaryColor,
  );

  return {
    primaryColor,
    secondaryColor: normalizeColor(
      themeAndColors?.secondaryColor,
      DEFAULT_THEME_AND_COLORS.secondaryColor,
    ),
    primaryFontColor: normalizeColor(
      themeAndColors?.primaryFontColor,
      DEFAULT_THEME_AND_COLORS.primaryFontColor,
    ),
    secondaryFontColor: normalizeSecondaryFontColor(
      themeAndColors?.secondaryFontColor,
      DEFAULT_THEME_AND_COLORS.secondaryFontColor,
    ),
    headerBackgroundColor: normalizeColor(
      themeAndColors?.headerBackgroundColor,
      primaryColor,
    ),
  };
}
